import type { MarketingStep } from "./types";

/** Новая кампания получает новые ID; связи резервных шагов остаются внутри копии. */
export function copyStepsForNewCampaign(
  steps: MarketingStep[],
): MarketingStep[] {
  const copies = structuredClone(steps);
  const ids = new Map(copies.map((step) => [step.id, crypto.randomUUID()]));
  // Не скрываем сломанную связь: её исправят в редакторе перед сохранением.
  const remap = (id: string | undefined) => (id ? (ids.get(id) ?? id) : id);
  return copies.map((step) => {
    const copy: MarketingStep = {
      ...step,
      id: ids.get(step.id)!,
      nextStepId: remap(step.nextStepId),
    };
    if (step.unreadFallback) {
      copy.unreadFallback = {
        ...step.unreadFallback,
        targetStepId: remap(step.unreadFallback.targetStepId) ?? "",
      };
    }
    if (step.split) {
      copy.split = {
        ...step.split,
        trueStepId: remap(step.split.trueStepId),
        falseStepId: remap(step.split.falseStepId),
      };
    }
    if (step.contactLimit)
      copy.contactLimit = {
        ...step.contactLimit,
        passStepId: remap(step.contactLimit.passStepId),
        stopStepId: remap(step.contactLimit.stopStepId),
      };
    if (step.cohorts) {
      copy.cohorts = step.cohorts.map((cohort) => ({
        ...cohort,
        stepId: remap(cohort.stepId),
      }));
    }
    return copy;
  });
}

export function unreadPushFallbackErrors(
  steps: MarketingStep[],
  index: number,
): string[] {
  const step = steps[index];
  const policy = step?.unreadFallback;
  if (!policy) return [];
  const errors: string[] = [];
  if (step.channel !== "push")
    errors.push(
      "резервная SMS по отсутствию прочтения доступна только у пуша. Отключите эту настройку или верните канал «Пуш приложения».",
    );
  if (
    !Number.isInteger(policy.afterMinutes) ||
    policy.afterMinutes < 1 ||
    policy.afterMinutes > 1440
  )
    errors.push("для резервной SMS укажите целое число минут от 1 до 1440.");
  const target = steps.findIndex((item) => item.id === policy.targetStepId);
  if (target <= index || steps[target]?.channel !== "sms")
    errors.push(
      "выберите резервный SMS-шаг, который расположен после пуша. Если вы удалили или переместили его, выберите другой шаг либо отключите резервную SMS.",
    );
  return errors;
}

export function defaultUnreadPushFallback(
  steps: MarketingStep[],
  index: number,
): NonNullable<MarketingStep["unreadFallback"]> {
  const candidates = steps
    .slice(index + 1)
    .filter((step) => step.channel === "sms");
  return {
    afterMinutes: 15,
    targetStepId: candidates.length === 1 ? candidates[0].id : "",
  };
}
